import React from 'react';
import PropTypes from 'prop-types';
import { getTypeInfo } from 'credit-card-type';
import { useSelector } from 'react-redux';

import { normalizePaymentType } from 'helpers/MyAccountUtils';
import useMartyContext from 'hooks/useMartyContext';
import AmazonPayLogo from 'images/amazon-pay.svg';
import AfterpayLogo from 'images/after-pay.svg';
import PaypalLogo from 'images/paypal-logo.png';
import { selectAuthentication } from 'selectors/authentication';
import { ApplePayOrder } from 'components/account/Order/ApplePayOrder';
import { cn } from 'helpers/classnames';

import css from 'styles/components/account/orderInformation.scss';

const makeShippingAddress = (address, testId) => {
  if (address) {
    const { fullName, addressLine1, addressLine2, city, stateOrRegion, postalCode, countryName } = address;
    return (
      <div className="zappos:body-m">
        <h2 className="zappos:body-l-bold"> Shipping Address</h2>
        <div data-test-id={testId('shippingName')}>{fullName}</div>
        <div data-test-id={testId('shippingLine1')}>{addressLine1}</div>
        <div data-test-id={testId('shippingLine2')}>{addressLine2}</div>
        <div data-test-id={testId('shippingCityState')}>
          {city}, {stateOrRegion}, {postalCode}
        </div>
        <div data-test-id={testId('shippingCountry')}>{countryName}</div>
      </div>
    );
  } else {
    return <div data-test-id={testId('electronicDelivery')}>This order was delivered electronically.</div>;
  }
};

const makeBillingAddress = (billingAddress, testId) => {
  const { fullName, addressLine1, addressLine2, city, stateOrRegion, postalCode, countryName } = billingAddress;
  if (billingAddress) {
    return (
      <div>
        <div data-test-id={testId('billingName')}>{fullName}</div>
        <div data-test-id={testId('billingLine1')}>{addressLine1}</div>
        <div data-test-id={testId('billingLine2')}>{addressLine2}</div>
        <div data-test-id={testId('billingCityState')}>
          {city}, {stateOrRegion}, {postalCode}
        </div>
        <div data-test-id={testId('billingCountry')}>{countryName}</div>
      </div>
    );
  }
};

const makePayment = ({ payment, billingAddress, testId, applePayOrder = false, amazonPayOrder = false, paypalOrder = false }) => {
  if (paypalOrder) {
    return (
      <div>
        <h2 className="zappos:body-l-bold">Payment Information</h2>
        <p>
          <img src={PaypalLogo} role="presentation" alt="This is a PayPal order" aria-label="PayPal Credit" width="120" />
        </p>
      </div>
    );
  }

  if (payment) {
    const { paymentType, addCreditCardNumber, expirationMonth, expirationYear, issuingBank = '' } = payment;
    const cardType = getTypeInfo(normalizePaymentType(paymentType))?.niceType;
    const isAfterpay = issuingBank.toLowerCase().includes('afterpay');

    switch (true) {
      case amazonPayOrder:
        return (
          <div className="zappos:body-m">
            <h2 className="zappos:body-l-bold">Payment Information</h2>
            <p>
              <img src={AmazonPayLogo} width="100" alt="This is an Amazon Pay order" />
            </p>
            <div data-test-id={testId('paymentCardInfo')}>
              {cardType} ending in {addCreditCardNumber}
            </div>
            <div data-test-id={testId('paymentCardExp')}>
              Exp. {expirationMonth}-{expirationYear}
            </div>
            {makeBillingAddress(billingAddress, testId)}
          </div>
        );
      case applePayOrder:
        return (
          <div>
            <h2 className="zappos:body-l-bold">Payment Information</h2>
            <ApplePayOrder />
          </div>
        );
      case isAfterpay:
        return (
          <div>
            <h2 className="zappos:body-l-bold">Payment Information</h2>
            <p>
              <img src={AfterpayLogo} width="100" alt="This is an Afterpay order" />
            </p>
            {makeBillingAddress(billingAddress, testId)}
          </div>
        );
      default:
        return (
          <div className="zappos:body-m">
            <h2 className="zappos:body-l-bold">Payment Information</h2>
            <div data-test-id={testId('paymentCardInfo')}>
              {cardType} ending in {addCreditCardNumber}
            </div>
            <div data-test-id={testId('paymentCardExp')}>
              Exp. {expirationMonth}-{expirationYear}
            </div>
            {makeBillingAddress(billingAddress, testId)}
          </div>
        );
    }
  }
};

export const OrderInformation = ({ order, address, payment, paymentUpdated }) => {
  const { billingAddress, orderId, orderDate, legacy, applePayOrder, amazonPayOrder, paypalOrder } = order;
  const authentication = useSelector(selectAuthentication);
  const {
    testId,
    marketplace: { dataMaskPiiElements }
  } = useMartyContext();
  return (
    <div className={css.orderInformation} data-cs-mask={dataMaskPiiElements}>
      {paymentUpdated && (
        <p className={cn(css.paymentUpdateSuccess, 'zappos:body-m')}>
          <strong>Success!</strong> You've updated your payment information. We will try to process the payment method again within 24 hours, and if
          successful, we will send you a shipment confirmation with your tracking information.
        </p>
      )}
      <div>
        <div className={css.orderNumber}>
          <h2 className="zappos:body-l-bold">Order Number</h2>
          <div className="zappos:body-m" data-test-id={testId('orderNumber')}>
            {orderId}
          </div>
        </div>
        <div>
          <h2 className="zappos:body-l-bold">Order Date</h2>
          <div className="zappos:body-m" data-test-id={testId('orderDate')}>
            {orderDate}
          </div>
        </div>
      </div>
      {!legacy && makeShippingAddress(address, testId)}
      {authentication &&
        makePayment({
          payment,
          billingAddress,
          testId,
          applePayOrder,
          amazonPayOrder,
          paypalOrder
        })}
    </div>
  );
};

OrderInformation.propTypes = {
  order: PropTypes.object.isRequired,
  address: PropTypes.object,
  payment: PropTypes.object
};

OrderInformation.displayName = 'OrderInformation';

export default OrderInformation;
